import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as PcmAPI from 'api/PCM';
import { unwrap, apiErrorMessage, notify } from 'utils/notify';

// ───────────────────────────────────────────────────────────────────────────
// PCM store — post-conversation analytics across workspaces
// (PCM_ANALYTICS_API.md §1–§5).
//
// Three read surfaces, one per screen:
//   summary       : one row per workspace                         (§1)
//   conversations : one workspace's closed conversations + PCM    (§2)
//   detail        : where a single conversation's PCM stands      (§3)
//
// `window` (dates, channels, include_test) is shared by the summary and the
// workspace list so the counts on one match the rows on the other. `query`
// holds the list-only filters and lives here rather than in the page so they
// survive a round trip to the detail screen.
//
// Each read keeps the requestId of its latest call: filters change faster than
// responses arrive, and a superseded (or aborted) request must not overwrite
// the newer one's state.
// ───────────────────────────────────────────────────────────────────────────

const initialQuery = {
  analytics_status: [],
  has_ai_agent: '',
  ai_agent_id: '',
  conversation_id: '',
  intent: '',
  disposition: '',
  page: 1,
  page_size: 10
};

const initialState = {
  window: { date_from: '', date_to: '', channel: [], include_test: false },
  summary: { data: null, loading: false, error: null, requestId: null },
  conversations: {
    schema: null,
    query: initialQuery,
    count: 0,
    results: [],
    summary: null,
    loading: false,
    error: null,
    requestId: null
  },
  detail: { key: null, data: null, loading: false, refreshing: false, error: null, requestId: null },
  // Keyed by `${schema}:${id}` so two conversations can be queued independently.
  regenerating: {},
  bulk: { loading: false, result: null, error: null }
};

export const pcmKey = (schema, id) => `${schema}:${id}`;

// Refusals carry a stable `code` in `data` (already_generated, in_progress,
// no_agent, no_transcript, enqueue_failed) — keep it next to the message.
const toFailure = (error, fallback) => ({
  message: apiErrorMessage(error, fallback),
  code: error?.response?.data?.data?.code || null,
  status: error?.response?.status || null
});

// ── thunks ────────────────────────────────────────────────────────────────

const getPcmSummary = createAsyncThunk('pcm/getSummary', async (filters, { signal, rejectWithValue }) => {
  try {
    return unwrap(await PcmAPI.getPcmSummary(filters, signal)) || {};
  } catch (error) {
    return rejectWithValue(toFailure(error, 'Failed to load the PCM summary.'));
  }
});

const getPcmConversations = createAsyncThunk('pcm/getConversations', async ({ schema, filters }, { signal, rejectWithValue }) => {
  try {
    return unwrap(await PcmAPI.getPcmConversations(schema, filters, signal)) || {};
  } catch (error) {
    return rejectWithValue(toFailure(error, 'Failed to load conversations.'));
  }
});

// `silent` re-reads without blanking the page — used after a regenerate and
// while polling for a queued run's result.
const getPcmDetail = createAsyncThunk('pcm/getDetail', async ({ schema, id }, { signal, rejectWithValue }) => {
  try {
    return unwrap(await PcmAPI.getPcmConversation(schema, id, signal)) || {};
  } catch (error) {
    return rejectWithValue(toFailure(error, 'Failed to load PCM for this conversation.'));
  }
});

// 202 means queued, not done — the caller re-reads the detail afterwards.
const regeneratePcm = createAsyncThunk('pcm/regenerate', async ({ schema, id, force }, { rejectWithValue }) => {
  try {
    const res = await PcmAPI.regeneratePcmConversation(schema, id, force ? { force: true } : {});
    notify.success(res?.data?.message || 'Analytics queued.');
    return unwrap(res) || {};
  } catch (error) {
    const failure = toFailure(error, 'Failed to queue analytics.');
    notify.error(failure.message);
    return rejectWithValue(failure);
  }
});

const regenerateMissingPcm = createAsyncThunk('pcm/regenerateMissing', async ({ schema, body }, { rejectWithValue }) => {
  try {
    const res = await PcmAPI.regenerateMissingPcm(schema, body);
    // A dry run queues nothing — its result is shown in the dialog, not toasted.
    if (!body?.dry_run) notify.success(res?.data?.message || 'Analytics queued.');
    return unwrap(res) || {};
  } catch (error) {
    const failure = toFailure(error, 'Failed to queue missing analytics.');
    notify.error(failure.message);
    return rejectWithValue(failure);
  }
});

// ── slice ─────────────────────────────────────────────────────────────────

const isLatest = (slot, action) => slot.requestId === action.meta.requestId;

const failureMessage = (action) => action.payload?.message || action.error?.message || 'Request failed.';

const PcmSlice = createSlice({
  name: 'pcm',
  initialState,
  reducers: {
    resetState: () => initialState,
    setWindow: (state, action) => {
      state.window = { ...state.window, ...action.payload };
      state.conversations.query.page = 1;
    },
    // Any filter change goes back to page 1 unless the change *is* the page.
    setListQuery: (state, action) => {
      const next = { ...state.conversations.query, ...action.payload };
      if (!('page' in action.payload)) next.page = 1;
      state.conversations.query = next;
    },
    // Back to the backend defaults; the chosen page size is a view preference, not a filter.
    resetFilters: (state) => {
      state.window = initialState.window;
      state.conversations.query = { ...initialQuery, page_size: state.conversations.query.page_size };
    },
    // Entering a different workspace drops the previous one's rows and filters.
    openWorkspace: (state, action) => {
      state.conversations = { ...initialState.conversations, schema: action.payload };
      state.bulk = initialState.bulk;
    },
    clearBulk: (state) => {
      state.bulk = initialState.bulk;
    }
  },
  extraReducers: {
    [getPcmSummary.pending]: (state, action) => {
      state.summary.loading = true;
      state.summary.error = null;
      state.summary.requestId = action.meta.requestId;
    },
    [getPcmSummary.fulfilled]: (state, action) => {
      if (!isLatest(state.summary, action)) return;
      state.summary.loading = false;
      state.summary.data = action.payload;
    },
    [getPcmSummary.rejected]: (state, action) => {
      if (!isLatest(state.summary, action)) return;
      state.summary.loading = false;
      if (!action.meta.aborted) state.summary.error = failureMessage(action);
    },

    [getPcmConversations.pending]: (state, action) => {
      state.conversations.loading = true;
      state.conversations.error = null;
      state.conversations.requestId = action.meta.requestId;
    },
    [getPcmConversations.fulfilled]: (state, action) => {
      if (!isLatest(state.conversations, action)) return;
      const payload = action.payload || {};
      state.conversations.loading = false;
      state.conversations.count = payload.count ?? payload.results?.length ?? 0;
      state.conversations.results = payload.results || [];
      state.conversations.summary = payload.summary || null;
    },
    [getPcmConversations.rejected]: (state, action) => {
      if (!isLatest(state.conversations, action)) return;
      state.conversations.loading = false;
      if (!action.meta.aborted) state.conversations.error = failureMessage(action);
    },

    [getPcmDetail.pending]: (state, action) => {
      const { schema, id, silent } = action.meta.arg;
      const key = pcmKey(schema, id);
      if (state.detail.key !== key) {
        state.detail.key = key;
        state.detail.data = null;
      }
      state.detail.loading = !silent || !state.detail.data;
      state.detail.refreshing = true;
      state.detail.error = null;
      state.detail.requestId = action.meta.requestId;
    },
    [getPcmDetail.fulfilled]: (state, action) => {
      if (!isLatest(state.detail, action)) return;
      state.detail.loading = false;
      state.detail.refreshing = false;
      state.detail.data = action.payload;
    },
    [getPcmDetail.rejected]: (state, action) => {
      if (!isLatest(state.detail, action)) return;
      state.detail.loading = false;
      state.detail.refreshing = false;
      if (!action.meta.aborted) state.detail.error = failureMessage(action);
    },

    [regeneratePcm.pending]: (state, action) => {
      const { schema, id } = action.meta.arg;
      state.regenerating[pcmKey(schema, id)] = true;
    },
    [regeneratePcm.fulfilled]: (state, action) => {
      const { schema, id } = action.meta.arg;
      const key = pcmKey(schema, id);
      delete state.regenerating[key];
      // Reflect "queued" straight away; the follow-up read brings the rest.
      if (state.detail.key === key && state.detail.data) state.detail.data.in_progress = true;
      if (state.conversations.schema === schema) {
        const row = state.conversations.results.find((r) => String(r.conversation_id) === String(id));
        if (row) row.in_progress = true;
      }
    },
    [regeneratePcm.rejected]: (state, action) => {
      const { schema, id } = action.meta.arg;
      delete state.regenerating[pcmKey(schema, id)];
    },

    [regenerateMissingPcm.pending]: (state) => {
      state.bulk.loading = true;
      state.bulk.error = null;
    },
    [regenerateMissingPcm.fulfilled]: (state, action) => {
      state.bulk.loading = false;
      state.bulk.result = action.payload;
    },
    [regenerateMissingPcm.rejected]: (state, action) => {
      state.bulk.loading = false;
      state.bulk.error = failureMessage(action);
    }
  }
});

const { resetState, setWindow, setListQuery, resetFilters, openWorkspace, clearBulk } = PcmSlice.actions;

// ── selectors ─────────────────────────────────────────────────────────────

export const selectPcmWindow = (state) => state.pcm.window;
export const selectPcmSummary = (state) => state.pcm.summary;
export const selectPcmConversations = (state) => state.pcm.conversations;
export const selectPcmDetail = (state) => state.pcm.detail;
export const selectPcmBulk = (state) => state.pcm.bulk;
export const selectPcmRegenerating = (schema, id) => (state) => !!state.pcm.regenerating[pcmKey(schema, id)];
// The workspace's display name, when the summary that lists it is loaded.
export const selectPcmWorkspace = (schema) => (state) => state.pcm.summary.data?.tenants?.find((t) => t.schema_name === schema) || null;

export default PcmSlice.reducer;

export {
  resetState,
  setWindow,
  setListQuery,
  resetFilters,
  openWorkspace,
  clearBulk,
  getPcmSummary,
  getPcmConversations,
  getPcmDetail,
  regeneratePcm,
  regenerateMissingPcm
};
